<template>
  <div class="rm-select" ref="selectRoot" :class="{ 'rm-select--disabled': disabled }">
    <div
      class="rm-select__control"
      :class="{ 'rm-select__control--focused': isOpen }"
      @click="toggleDropdown"
      @keydown="handleKeydown"
      tabindex="0"
      ref="control"
    >
      <div class="rm-select__selections" ref="selections">
        <template v-if="multiple && selectedItems.length">
          <span
            v-for="item in visibleChips"
            :key="getItemValue(item)"
            :class="{ 'rm-select__chip': chips, 'rm-select__chip--plain': !chips, 'rm-select__chip--small': smallChips }"
          >
            <span class="rm-select__chip-text">{{ getItemText(item) }}</span>
            <button
              v-if="deletableChips"
              class="rm-select__chip-remove"
              @click.stop="removeItem(item)"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </span>
          <span
            v-if="overflowCount > 0"
            class="rm-select__chip rm-select__chip--overflow"
            :class="{ 'rm-select__chip--small': chips && smallChips }"
            ref="overflowChip"
            @mouseenter="showOverflowTooltip"
            @mouseleave="hideOverflowTooltip"
          >
            +{{ overflowCount }}
          </span>
          <div v-show="overflowTooltipVisible" class="rm-select__overflow-tooltip" :style="overflowTooltipStyle">
            <div v-for="item in overflowChips" :key="getItemValue(item)">{{ getItemText(item) }}</div>
          </div>
        </template>

        <span v-else-if="!multiple && hasValue" class="rm-select__single-value">
          {{ displayText }}
        </span>

        <span
          v-if="showPlaceholder"
          class="rm-select__placeholder"
          :style="{ color: placeholderColor }"
        >{{ placeholder }}</span>
      </div>

      <button
        v-if="clearable && hasValue"
        class="rm-select__clear"
        @click.stop="clearSelection"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div class="rm-select__append">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          :class="{ 'rm-select__arrow--open': isOpen }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
    </div>

    <div
      v-show="isOpen"
      class="rm-select__menu"
      :style="menuStyle"
      ref="menu"
    >
      <div class="rm-select__menu-content">
        <div
          v-for="(item, index) in items"
          :key="getItemValue(item)"
          class="rm-select__item"
          :class="{
            'rm-select__item--selected': isSelected(item),
            'rm-select__item--highlighted': highlightedIndex === index,
          }"
          @click.stop="selectItem(item)"
          @mouseenter="highlightedIndex = index"
        >
          <slot name="item" :item="item">
            <div class="rm-select__item-content">
              <span v-if="multiple" class="rm-select__checkbox">
                <svg v-if="isSelected(item)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="4 12 10 18 20 6" />
                </svg>
              </span>
              <span>{{ getItemText(item) }}</span>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';

export default {
  name: 'RmSelect',
  props: {
    value: {
      type: [String, Number, Array, Object],
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    itemText: {
      type: String,
      default: 'text',
    },
    itemValue: {
      type: String,
      default: 'value',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    chips: {
      type: Boolean,
      default: false,
    },
    smallChips: {
      type: Boolean,
      default: false,
    },
    deletableChips: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    placeholderColor: {
      type: String,
      default: '#000000',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const selectRoot = ref(null);
    const control = ref(null);
    const menu = ref(null);
    const selections = ref(null);
    const isOpen = ref(false);
    const highlightedIndex = ref(-1);
    const menuStyle = ref({});
    const overflowChip = ref(null);
    const maxVisibleChips = ref(Infinity);
    const overflowTooltipVisible = ref(false);
    const overflowTooltipStyle = ref({});

    const getItemText = (item) => {
      if (item == null) return '';
      if (typeof item === 'string' || typeof item === 'number') return String(item);
      return item[props.itemText] ?? '';
    };

    const getItemValue = (item) => {
      if (item == null) return item;
      if (typeof item === 'string' || typeof item === 'number') return item;
      return item[props.itemValue];
    };

    const selectedItems = computed(() => {
      if (props.multiple) {
        const vals = Array.isArray(props.value) ? props.value : [];
        return vals.map((v) => props.items.find((i) => getItemValue(i) === v)).filter(Boolean);
      }
      if (props.value == null) return [];
      const found = props.items.find((i) => getItemValue(i) === props.value);
      return found ? [found] : [];
    });

    const hasValue = computed(() => {
      if (props.multiple) return Array.isArray(props.value) && props.value.length > 0;
      return props.value != null;
    });

    const displayText = computed(() => {
      if (props.multiple) return '';
      if (!hasValue.value) return '';
      const item = selectedItems.value[0];
      return item ? getItemText(item) : '';
    });

    const showPlaceholder = computed(() => {
      if (props.multiple) return !hasValue.value;
      return !hasValue.value;
    });

    const visibleChips = computed(() => {
      return selectedItems.value.slice(0, maxVisibleChips.value);
    });

    const overflowChips = computed(() => {
      return selectedItems.value.slice(maxVisibleChips.value);
    });

    const overflowCount = computed(() => {
      return Math.max(0, selectedItems.value.length - maxVisibleChips.value);
    });

    const calculateVisibleChips = () => {
      if (!props.multiple || !selections.value) {
        maxVisibleChips.value = Infinity;
        return;
      }
      nextTick(() => {
        const container = selections.value;
        if (!container) return;
        const containerWidth = container.offsetWidth;
        // Reserve space for the "+N" chip and the clear/arrow buttons
        const reservedWidth = 60;
        const chips = container.querySelectorAll('.rm-select__chip:not(.rm-select__chip--overflow)');
        let usedWidth = 0;
        let fitCount = 0;

        for (const chip of chips) {
          usedWidth += chip.offsetWidth + 4; // 4px gap
          if (usedWidth > containerWidth - reservedWidth && fitCount > 0) break;
          fitCount++;
        }

        // Always show at least 1 chip
        maxVisibleChips.value = Math.max(1, fitCount);
      });
    };

    const showOverflowTooltip = () => {
      if (!overflowChip.value) return;
      const rect = overflowChip.value.getBoundingClientRect();
      overflowTooltipStyle.value = {
        position: 'fixed',
        left: `${rect.left + rect.width / 2}px`,
        top: `${rect.top - 6}px`,
        transform: 'translate(-50%, -100%)',
      };
      overflowTooltipVisible.value = true;
    };

    const hideOverflowTooltip = () => {
      overflowTooltipVisible.value = false;
    };

    watch(() => selectedItems.value.length, () => {
      maxVisibleChips.value = Infinity;
      nextTick(calculateVisibleChips);
    });

    onMounted(() => {
      calculateVisibleChips();
    });

    const isSelected = (item) => {
      const val = getItemValue(item);
      if (props.multiple) {
        return Array.isArray(props.value) && props.value.includes(val);
      }
      return props.value === val;
    };

    const calculateMenuPosition = () => {
      if (!control.value) return;
      const rect = control.value.getBoundingClientRect();
      const maxHeight = 256;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openBelow = spaceBelow >= maxHeight || spaceBelow >= spaceAbove;

      menuStyle.value = {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        maxHeight: `${Math.min(maxHeight, openBelow ? spaceBelow - 8 : spaceAbove - 8)}px`,
        ...(openBelow
          ? { top: `${rect.bottom + 2}px` }
          : { bottom: `${window.innerHeight - rect.top + 2}px`, top: 'auto' }),
      };
    };

    const handleOutsideClick = (e) => {
      if (selectRoot.value && !selectRoot.value.contains(e.target)) {
        closeDropdown();
      }
    };

    const handleScroll = () => {
      calculateMenuPosition();
    };

    const openDropdown = () => {
      if (props.disabled) return;
      isOpen.value = true;
      highlightedIndex.value = -1;
      nextTick(() => {
        calculateMenuPosition();
        document.addEventListener('mousedown', handleOutsideClick);
        window.addEventListener('scroll', handleScroll, true);
      });
    };

    const closeDropdown = () => {
      isOpen.value = false;
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('scroll', handleScroll, true);
    };

    const toggleDropdown = () => {
      if (props.disabled) return;
      isOpen.value ? closeDropdown() : openDropdown();
    };

    const selectItem = (item) => {
      const val = getItemValue(item);
      if (props.multiple) {
        const current = Array.isArray(props.value) ? [...props.value] : [];
        const idx = current.indexOf(val);
        if (idx === -1) {
          current.push(val);
        } else {
          current.splice(idx, 1);
        }
        emit('input', current);
        emit('change', current);
      } else {
        emit('input', val);
        emit('change', val);
        closeDropdown();
      }
    };

    const removeItem = (item) => {
      if (props.disabled) return;
      const val = getItemValue(item);
      const current = Array.isArray(props.value) ? props.value.filter((v) => v !== val) : [];
      emit('input', current);
      emit('change', current);
    };

    const clearSelection = () => {
      if (props.disabled) return;
      emit('input', props.multiple ? [] : null);
      emit('change', props.multiple ? [] : null);
    };

    const scrollHighlightedIntoView = () => {
      nextTick(() => {
        if (!menu.value) return;
        const items = menu.value.querySelectorAll('.rm-select__item');
        const target = items[highlightedIndex.value];
        if (target) target.scrollIntoView({ block: 'nearest' });
      });
    };

    const handleKeydown = (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen.value) {
            openDropdown();
          } else {
            highlightedIndex.value = Math.min(highlightedIndex.value + 1, props.items.length - 1);
            scrollHighlightedIntoView();
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (isOpen.value) {
            highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
            scrollHighlightedIntoView();
          }
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (isOpen.value && highlightedIndex.value >= 0) {
            selectItem(props.items[highlightedIndex.value]);
          } else if (!isOpen.value) {
            openDropdown();
          }
          break;
        case 'Escape':
          e.preventDefault();
          closeDropdown();
          break;
        case 'Tab':
          closeDropdown();
          break;
      }
    };

    onBeforeUnmount(() => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('scroll', handleScroll, true);
    });

    return {
      selectRoot,
      control,
      menu,
      selections,
      overflowChip,
      isOpen,
      highlightedIndex,
      menuStyle,
      overflowTooltipStyle,
      selectedItems,
      hasValue,
      displayText,
      showPlaceholder,
      visibleChips,
      overflowChips,
      overflowCount,
      overflowTooltipVisible,
      showOverflowTooltip,
      hideOverflowTooltip,
      getItemText,
      getItemValue,
      isSelected,
      toggleDropdown,
      selectItem,
      removeItem,
      clearSelection,
      handleKeydown,
    };
  },
};
</script>

<style scoped lang="scss">
.rm-select {
  position: relative;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  width: 100%;

  &--disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.rm-select__control {
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: 4px 8px 4px 12px;
  border: 1px solid #D9D9D9;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  transition: border-color 0.2s;
  gap: 4px;
  outline: none;

  &:hover {
    border-color: #999;
  }

  &--focused {
    border-color: #1976D2;
  }
}

.rm-select__selections {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.rm-select__placeholder {
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rm-select__single-value {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #000;
}

.rm-select__chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background-color: #E0E0E0;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.4;
  white-space: nowrap;
  max-width: 150px;
  flex-shrink: 0;

  &--plain {
    border: none;
  }

  &--small {
    padding: 1px 6px;
    font-size: 12px;
    border-radius: 10px;
  }

  &--overflow {
    position: relative;
    cursor: default;
    font-weight: 600;
    padding: 2px 4px;
    border-radius: 4px;
  }
}

.rm-select__overflow-tooltip {
  background-color: #6b6a6a;
  color: #fff;
  font-size: 12px;
  font-weight: 400;
  padding: 6px 10px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 1001;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #333;
  }
}

.rm-select__chip-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

.rm-select__chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: #666;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    color: #333;
  }

  svg {
    width: 12px;
    height: 12px;
  }
}

.rm-select__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: #999;
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  &:hover {
    color: #333;
  }

  svg {
    width: 16px;
    height: 16px;
  }
}

.rm-select__append {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: #999;
  margin-left: 4px;

  svg {
    width: 18px;
    height: 18px;
    transition: transform 0.2s;
  }
}

.rm-select__arrow--open {
  transform: rotate(180deg);
}

.rm-select__menu {
  z-index: 1000;
  background-color: #fff;
  border: 1px solid #D9D9D9;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  overflow-y: auto;
}

.rm-select__menu-content {
  padding: 4px 0;
}

.rm-select__item {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  color: #000;
  transition: background-color 0.1s;

  &--highlighted {
    background-color: #f5f5f5;
  }

  &--selected {
    background-color: #E3F2FD;
  }
}

.rm-select__item-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rm-select__checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 2px solid #999;
  border-radius: 3px;
  flex-shrink: 0;

  .rm-select__item--selected & {
    background-color: #1976D2;
    border-color: #1976D2;
    color: #fff;
  }

  svg {
    width: 12px;
    height: 12px;
  }
}
</style>
